import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  TextInput,
  BooleanInput,
  RaRecord,
} from 'react-admin';
import { Alert, Stack } from '@mui/material';
import { AvailabilityCell, CompanyCell, PayloadCell } from './cells';
import { VehicleInlineCell, formatVehicleYear } from './listCells';
import { ChipCell, NoWrap, compactDatagridSx } from '../../shared/listCells';
import { SyncStatus } from '../SyncStatus';
import { TRAILER_FILTER } from './vehicleTypes';
import { menuLabel } from '../menuConfig';
import { ListExportActions } from '../../shared/ListExportActions';
import { trailerExporter, trailerExporterLegacy } from './exportColumns';
import { INCLUDE_ARCHIVED_FILTER, INCLUDE_ARCHIVED_LABEL } from './archivedVehicles';
import { useFeature } from '../featureFlags/FeatureGate';
import { FLEET_HIDE_ARCHIVED, OPERATIONAL_LABEL } from './fleetFeatures';

/**
 * Filtry według przełączników konta (12.10.2026) — wyłączone dają filtry
 * jak na produkcji 0.31.3 (bez „Pokaż także archiwalne”, „Tylko sprawne”).
 */
export const trailerFilters = ({
  hideArchived,
  operationalLabel,
}: {
  hideArchived: boolean;
  operationalLabel: boolean;
}) => [
  <TextInput source="q" label="Szukaj (numer / model)" alwaysOn />,
  // Ta sama reguła co „Flota”: bez przełącznika API pomija archiwalne (07.10.2026).
  ...(hideArchived
    ? [<BooleanInput source={INCLUDE_ARCHIVED_FILTER} label={INCLUDE_ARCHIVED_LABEL} alwaysOn />]
    : []),
  // `defaultValue`: filtr dodany z „Dodaj filtr” od razu działa (bez
  // tego pojawiał się wyłączony i trzeba było go przełączyć, e2e 28.09.2026).
  <BooleanInput source="available" label="Tylko dostępne" defaultValue />,
  // „W eksploatacji” jak na liście floty (08.10.2026) — klucz bez zmian.
  <BooleanInput
    source="operational"
    label={operationalLabel ? 'Tylko w eksploatacji' : 'Tylko sprawne'}
    defaultValue
  />,
  <BooleanInput source="bezSpolki" label="Bez spółki" defaultValue />,
];

/**
 * Naczepy — widok na kartotekę pojazdów zawężony typem.
 *
 * ## Dlaczego to nie jest osobny zasób
 *
 * Bo klient nie ma naczep osobno. Jego interfejs wystawia **jeden endpoint
 * pojazdów**, a naczepy siedzą w nim razem z ciągnikami, rozróżnione wyłącznie
 * typem — 580 z 1212 rekordów na danych z 22.09.2026.
 *
 * Tabela `Trailer` w naszym schemacie (port z FreightFusion) ma **zero wierszy**
 * i nic jej nie zapełnia. Ekran zbudowany na niej pokazywałby pustą listę
 * i wyglądał na zepsuty, choć naczepy w systemie są.
 *
 * Wiersz prowadzi do **tej samej karty pojazdu** co flota: to jeden rekord
 * w kartotece klienta, więc drugi widok szczegółów byłby drugą wersją prawdy.
 *
 * Kolumny różnią się od floty tym, czego przy naczepie nie ma: nie pokazujemy
 * kierowcy, bo kierowca siedzi przy ciągniku.
 */
export const TrailerList = () => (
  <TrailerListView hideArchived={useFeature(FLEET_HIDE_ARCHIVED)} operationalLabel={useFeature(OPERATIONAL_LABEL)} />
);

const TrailerListView = ({ hideArchived, operationalLabel }: { hideArchived: boolean; operationalLabel: boolean }) => (
  <List
    resource="cars"
    filter={TRAILER_FILTER}
    filters={trailerFilters({ hideArchived, operationalLabel })}
    // Bez własnego klucza lista dzieliłaby zapamiętane filtry z „Flotą”
    // (ten sam zasób `cars`): szukajka wpisana tu wracała tam.
    storeKey="trailers.listParams"
    exporter={operationalLabel ? trailerExporter : trailerExporterLegacy}
    actions={<ListExportActions />}
    sort={{ field: 'registrationNumber', order: 'ASC' }}
    perPage={25}
    title={menuLabel('trailers')}
  >
    <Stack spacing={1}>
      <SyncStatus />
      <Alert severity="info" sx={{ py: 0.25 }}>
        TMS prowadzi naczepy w tej samej kartotece co pojazdy, dlatego karta
        naczepy wygląda tak samo jak karta pojazdu.
      </Alert>
      <Datagrid rowClick="show" bulkActionButtons={false} sx={compactDatagridSx}>
        <FunctionField
          label="Naczepa"
          render={(r: RaRecord) => <VehicleInlineCell record={r} />}
        />
        <TextField source="trailerType" label="Typ naczepy" sx={{ whiteSpace: 'nowrap' }} />
        <FunctionField
          label="Dostępność"
          render={(r: RaRecord) => (
            <ChipCell max={220}>
              <AvailabilityCell record={r} />
            </ChipCell>
          )}
        />
        <FunctionField
          label="Ładowność"
          render={(r: RaRecord) => (
            <NoWrap>
              <PayloadCell record={r} />
            </NoWrap>
          )}
        />
        {/* `source` zostaje: po nim się sortuje. Rok 0 z TMS to myślnik. */}
        <FunctionField
          source="vehicleYear"
          label="Rocznik"
          render={(r: RaRecord) => formatVehicleYear(r.vehicleYear)}
        />
        <FunctionField label="Spółka" render={(r: RaRecord) => <CompanyCell record={r} />} />
      </Datagrid>
    </Stack>
  </List>
);
